import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";

const currency = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

const STATUS_LABEL = {
  pendiente: "Pendiente",
  confirmado: "Confirmado",
  en_diseño: "En Diseño",
  en_produccion: "En Producción",
  listo: "Listo",
  enviado: "Enviado",
  entregado: "Entregado",
  cancelado: "Cancelado",
};

const STATUS_COLOR = {
  pendiente: "#F59E0B",
  confirmado: "#3B82F6",
  en_diseño: "#8B5CF6",
  en_produccion: "#EC4899",
  listo: "#10B981",
  enviado: "#06B6D4",
  entregado: "#22C55E",
  cancelado: "#71717A",
};

/**
 * Generate an elegant PDF invoice/order confirmation.
 * @param {object} order
 * @param {object} settings - site settings from SettingsContext
 */
export async function generateOrderPDF(order, settings) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginL = 15;
  const marginR = 15;
  const contentWidth = pageWidth - marginL - marginR;

  // Order number label — customer-facing folio (defaults to order_id when new numeration missing)
  const displayNumber = order.order_number ? `#${order.order_number}` : order.order_id.toUpperCase();

  // Build QR code that points to the customer's public order panel
  // (scan → cliente ve su pedido y puede autorizarlo). Only shown for new
  // sequentially-numbered orders (>= 1001); legacy `o_...` orders skip the QR.
  let qrDataUrl = null;
  let publicOrderUrl = null;
  try {
    const origin = (typeof window !== "undefined" && window.location?.origin) || "";
    if (order.order_number) {
      publicOrderUrl = `${origin}/mi-pedido/${order.order_number}`;
      qrDataUrl = await QRCode.toDataURL(publicOrderUrl, { margin: 1, width: 220, color: { dark: "#0A0A0A", light: "#FFFFFF" } });
    }
  } catch (_e) {
    qrDataUrl = null;
  }
  const accent = settings.brand_accent || "#FF3B30";
  const [ar, ag, ab] = hexToRgb(accent);

  // ==== HEADER ====
  // Left: brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(15, 15, 15);
  doc.text((settings.brand_name || "PRINTLAB").toUpperCase(), marginL, 22);
  doc.setTextColor(ar, ag, ab);
  doc.setFontSize(28);
  doc.text(".", marginL + doc.getTextWidth((settings.brand_name || "PRINTLAB").toUpperCase()) + 0.5, 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(115, 115, 115);
  doc.text(settings.company_name || "", marginL, 27);

  // Right: doc title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(15, 15, 15);
  doc.text("COMPROBANTE DE PEDIDO", pageWidth - marginR, 20, { align: "right" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(115, 115, 115);
  doc.text(
    new Date(order.created_at).toLocaleString("es-MX", { hour12: false }),
    pageWidth - marginR,
    26,
    { align: "right" }
  );

  // Divider
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.line(marginL, 32, pageWidth - marginR, 32);

  // ==== ORDER REFERENCE ====
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(115, 115, 115);
  doc.text("REFERENCIA DE PEDIDO", marginL, 40);
  doc.setFont("courier", "bold");
  doc.setFontSize(15);
  doc.setTextColor(ar, ag, ab);
  doc.text(displayNumber, marginL, 47);
  if (order.order_number) {
    doc.setFont("courier", "normal");
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    doc.text(order.order_id.toUpperCase(), marginL, 51.5);
  }

  // Right: status + delivery estimate
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(115, 115, 115);
  doc.text("ESTATUS", pageWidth - marginR, 40, { align: "right" });
  // Colored status pill (aligned right)
  const statusLabel = (STATUS_LABEL[order.status] || order.status || "—").toUpperCase();
  const statusColor = STATUS_COLOR[order.status] || "#0A0A0A";
  const [sr, sg, sb] = hexToRgb(statusColor);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const pillTextW = doc.getTextWidth(statusLabel);
  const pillPad = 3;
  const pillH = 5.5;
  const pillW = pillTextW + pillPad * 2;
  const pillX = pageWidth - marginR - pillW;
  const pillY = 43;
  doc.setFillColor(sr, sg, sb);
  doc.roundedRect(pillX, pillY, pillW, pillH, 1, 1, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(statusLabel, pillX + pillPad, pillY + pillH - 1.7);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(115, 115, 115);
  doc.text(`Entrega estimada: ${order.delivery_estimate || "—"}`, pageWidth - marginR, 53, { align: "right" });

  // ==== TWO-COLUMN INFO: COMPANY + CUSTOMER ====
  let y = 60;
  const colW = (contentWidth - 8) / 2;

  // Company block
  drawInfoBlock(doc, marginL, y, colW, "EMISOR", [
    settings.company_name || "",
    settings.company_address || "",
    settings.company_city || "",
    settings.company_phone ? `Tel: ${settings.company_phone}` : "",
    settings.company_whatsapp && settings.company_whatsapp !== settings.company_phone ? `WhatsApp: ${settings.company_whatsapp}` : "",
    settings.company_email ? `Correo: ${settings.company_email}` : "",
  ].filter(Boolean));

  // Customer block — layout adapts to delivery method
  const s = order.shipping || {};
  const isPickup = order.delivery_method === "sucursal";
  const customerLines = isPickup
    ? [
        s.name || order.user_name || "",
        s.phone ? `WhatsApp: ${s.phone}` : "",
        order.user_email || "",
        "Entrega: Recolección en sucursal",
      ]
    : [
        s.name || order.user_name || "",
        s.phone ? `WhatsApp: ${s.phone}` : "",
        s.address || "",
        [s.city, s.state, s.zip].filter(Boolean).join(", "),
        order.user_email || "",
      ];
  drawInfoBlock(doc, marginL + colW + 8, y, colW, isPickup ? "CLIENTE · RECOLECCIÓN" : "CLIENTE", customerLines.filter(Boolean));

  y += 42;

  // ==== ITEMS TABLE ====
  const rows = (order.items || []).map((it) => {
    const qty = Math.max(1, Number(it.quantity) || 1);
    const dsPrice = Number(it.design_service_price || 0);
    const extras = Array.isArray(it.selected_extra_services) ? it.selected_extra_services : [];
    const extrasTotal = extras.reduce((acc, e) => acc + Number(e.price || 0), 0);
    // Isolate the plain-product subtotal so UNITARIO reflects the real unit price
    const plainSubtotal = Number(it.line_total || 0) - dsPrice - extrasTotal;
    const unitPrice = plainSubtotal / qty;

    const desc = [];
    if (it.color_label) desc.push(`Color: ${it.color_label}`);
    if (it.size_label) desc.push(`Talla: ${it.size_label}`);
    if (it.printing_method_label) desc.push(`Impresión: ${it.printing_method_label}`);
    if (it.design_filename) desc.push(`Diseño: ${it.design_filename}`);
    if (it.notes) desc.push(`Notas: ${it.notes}`);
    const baseLine = it.product_name + (desc.length ? `\n${desc.join(" · ")}` : "");
    // Add-on breakdown lines (visible amounts so el cliente sabe qué se cobra)
    const addOns = [];
    if (dsPrice > 0) {
      addOns.push(`+ Servicio de diseño gráfico (único): ${currency(dsPrice)}`);
    }
    for (const e of extras) {
      addOns.push(`+ ${e.name} (único): ${currency(Number(e.price || 0))}`);
    }
    const fullDesc = baseLine + (addOns.length ? `\n${addOns.join("\n")}` : "");

    return [
      fullDesc,
      String(qty),
      currency(unitPrice),
      currency(it.line_total),
    ];
  });

  // Append ad-hoc / custom (quote-only) items — not tied to catalog products.
  for (const ci of (order.custom_items || [])) {
    const qty = Math.max(1, Number(ci.quantity) || 1);
    const desc = ci.name + (ci.description ? `\n${ci.description}` : "");
    rows.push([desc, String(qty), currency(ci.unit_price), currency(ci.line_total)]);
  }

  autoTable(doc, {
    head: [["PRODUCTO", "CANT.", "UNITARIO", "TOTAL"]],
    body: rows,
    startY: y,
    theme: "plain",
    headStyles: {
      fillColor: [15, 15, 15],
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: "bold",
      cellPadding: 3,
    },
    bodyStyles: { fontSize: 9, textColor: [30, 30, 30], cellPadding: { top: 3, right: 3, bottom: 3, left: 3 }, valign: "top" },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { cellWidth: contentWidth - 60 },
      1: { cellWidth: 15, halign: "center" },
      2: { cellWidth: 22, halign: "right" },
      3: { cellWidth: 23, halign: "right", fontStyle: "bold" },
    },
    margin: { left: marginL, right: marginR },
    styles: { lineColor: [230, 230, 230], lineWidth: 0.1 },
  });

  y = doc.lastAutoTable.finalY + 6;

  // ==== TOTALS ====
  const totalsX = pageWidth - marginR - 60;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  const line = (label, value, bold = false, color = null) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    if (color) doc.setTextColor(...color);
    else doc.setTextColor(80, 80, 80);
    doc.text(label, totalsX, y);
    doc.text(value, pageWidth - marginR, y, { align: "right" });
    y += 5;
  };
  line("Subtotal", currency(order.subtotal));
  if (order.delivery_method === "sucursal") {
    line("Entrega", "Recolección en sucursal");
  } else {
    line("Envío", order.shipping_fee > 0 ? currency(order.shipping_fee) : "Gratis");
  }
  if (order.iva_enabled && Number(order.iva_amount || 0) > 0) {
    line(`IVA (${Math.round((order.iva_rate || 0.16) * 100)}%)`, currency(order.iva_amount));
  }
  doc.setDrawColor(230, 230, 230);
  doc.line(totalsX, y - 2, pageWidth - marginR, y - 2);
  y += 1;
  doc.setFontSize(11);
  line("TOTAL", currency(order.total), true, [15, 15, 15]);
  if (order.manual_total_override != null) {
    doc.setFontSize(7);
    doc.setTextColor(255, 59, 48);
    doc.text("Total ajustado manualmente", pageWidth - marginR, y, { align: "right" });
    doc.setTextColor(15, 15, 15);
    y += 4;
  }
  doc.setFontSize(9);

  // ==== PAYMENT STATUS BLOCK (auto-adapts to deposit_paid / final_paid) ====
  const depositAmount = Number(order.deposit_amount || order.total * 0.5);
  const finalAmount = Number(order.total || 0) - depositAmount;
  const depPaid = !!order.deposit_paid;
  const finPaid = !!order.final_paid;
  const bothPaid = depPaid && finPaid;
  const nonePaid = !depPaid && !finPaid;

  const fmtVia = (v) => ({ transferencia: "Transferencia", efectivo: "Efectivo", stripe: "Tarjeta" }[v] || v || "—");
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("es-MX"); } catch { return ""; }
  };

  y += 4;
  const payY = y;
  const payH = 28;
  // Bar color: red (none paid) → amber (partial) → emerald (paid)
  const barColor = bothPaid ? [16, 185, 129] : (nonePaid ? [ar, ag, ab] : [245, 158, 11]);
  doc.setFillColor(barColor[0], barColor[1], barColor[2]);
  doc.rect(marginL, payY, contentWidth, 7.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const barTitle = bothPaid
    ? "PAGADO EN SU TOTALIDAD"
    : (nonePaid
      ? "ANTICIPO REQUERIDO (50%) PARA INICIAR TU PEDIDO"
      : "ANTICIPO RECIBIDO · FINIQUITO PENDIENTE");
  doc.text(barTitle, marginL + 4, payY + 5.2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const barRight = bothPaid ? currency(order.total) : currency(depPaid ? finalAmount : depositAmount);
  doc.text(barRight, pageWidth - marginR - 4, payY + 5.5, { align: "right" });

  // Two-column detail below bar
  doc.setFillColor(250, 250, 250);
  doc.rect(marginL, payY + 7.5, contentWidth, payH - 7.5, "F");
  const colWpay = (contentWidth - 4) / 2;
  const payRow = (x, label, amount, paid, via, at) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(label, x + 3, payY + 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(15, 15, 15);
    doc.text(currency(amount), x + 3, payY + 16.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    if (paid) {
      doc.setTextColor(4, 120, 87);
      doc.text(`✓ RECIBIDO · ${fmtVia(via)}${at ? ` · ${fmtDate(at)}` : ""}`, x + 3, payY + 21);
    } else {
      doc.setTextColor(180, 83, 9);
      doc.text("• PENDIENTE", x + 3, payY + 21);
    }
  };
  payRow(marginL, "ANTICIPO (50%)", depositAmount, depPaid, order.deposit_paid_via, order.deposit_paid_at);
  payRow(marginL + colWpay + 4, "FINIQUITO (50%)", finalAmount, finPaid, order.final_paid_via, order.final_paid_at);

  y = payY + payH + 4;

  // ==== BANK DETAILS ====
  const bankBlockH = 48;
  doc.setFillColor(250, 250, 250);
  doc.rect(marginL, y, contentWidth, bankBlockH, "F");
  doc.setTextColor(15, 15, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("DATOS PARA TRANSFERENCIA BANCARIA", marginL + 4, y + 6);

  // Two-column, two-row compact grid
  const bx1 = marginL + 4;
  const bx2 = marginL + contentWidth / 2 + 2;
  const colInnerW = contentWidth / 2 - 6;
  const row1Y = y + 13;
  const row2Y = y + 26;

  const bankLine = (x, rowY, label, value) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(130, 130, 130);
    doc.setFontSize(7);
    doc.text(label, x, rowY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 15, 15);
    doc.setFontSize(9);
    const wrapped = doc.splitTextToSize(String(value || "—"), colInnerW);
    doc.text(wrapped, x, rowY + 4);
  };

  bankLine(bx1, row1Y, "BANCO", settings.bank_name || "");
  bankLine(bx2, row1Y, "BENEFICIARIO", settings.bank_beneficiary || "");
  bankLine(bx1, row2Y, "CLABE / CUENTA", settings.bank_clabe || "");
  bankLine(bx2, row2Y, "REFERENCIA (concepto)", order.order_id.toUpperCase());

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(80, 80, 80);
  const noteY = y + bankBlockH - 4;
  doc.text(
    `Envía tu comprobante a ${settings.bank_email || settings.company_email || ""} para confirmar tu anticipo.`,
    marginL + 4,
    noteY
  );

  y += bankBlockH + 6;

  // ==== QR CODE (scan → cliente ve su pedido y lo autoriza) ====
  if (qrDataUrl && publicOrderUrl) {
    const qrSize = 32;
    const qrX = pageWidth - marginR - qrSize;
    const qrY = y;
    doc.setDrawColor(230, 230, 230);
    doc.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 10);
    doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(120, 120, 120);
    doc.text("ESCANEA PARA VER Y AUTORIZAR TU PEDIDO", qrX + qrSize / 2, qrY + qrSize + 4.5, { align: "center" });

    // Left column: authorization reminder + link + folio
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(ar, ag, ab);
    doc.text("IMPORTANTE: AUTORIZA TU PEDIDO", marginL, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(60, 60, 60);
    const authWrap = doc.splitTextToSize(
      "Escanea el código QR o abre el enlace para revisar y AUTORIZAR tu pedido. Necesitamos tu autorización para iniciar producción.",
      contentWidth - qrSize - 10
    );
    doc.text(authWrap, marginL, y + 10);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(130, 130, 130);
    doc.text("ENLACE DIRECTO", marginL, y + 22);
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(15, 15, 15);
    const linkWrap = doc.splitTextToSize(publicOrderUrl, contentWidth - qrSize - 10);
    doc.text(linkWrap, marginL, y + 26);
    try { doc.link(marginL, y + 23, contentWidth - qrSize - 10, 6, { url: publicOrderUrl }); } catch (_e) { /* noop */ }

    y += qrSize + 10;
  } else {
    // No QR (legacy order without order_number): still show the folio prominently.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(130, 130, 130);
    doc.text("FOLIO DE PEDIDO", marginL, y + 6);
    doc.setFont("courier", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 15, 15);
    doc.text(displayNumber, marginL, y + 15);
    y += 20;
  }

  // ==== FOOTER ====
  const footY = pageHeight - 15;
  doc.setDrawColor(230, 230, 230);
  doc.line(marginL, footY - 5, pageWidth - marginR, footY - 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(130, 130, 130);
  doc.text(
    `Gracias por elegir ${settings.brand_name || "PRINTLAB"}. Contáctanos: ${settings.company_phone || ""}${settings.company_email ? " · " + settings.company_email : ""}`,
    pageWidth / 2,
    footY,
    { align: "center" }
  );
  doc.text(
    `Documento generado el ${new Date().toLocaleString("es-MX")} · Ref. ${displayNumber}`,
    pageWidth / 2,
    footY + 4,
    { align: "center" }
  );

  doc.save(`Pedido-${order.order_number || order.order_id.toUpperCase()}.pdf`);
}

function drawInfoBlock(doc, x, y, w, title, lines) {
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.rect(x, y, w, 38);
  doc.setFontSize(7);
  doc.setTextColor(130, 130, 130);
  doc.setFont("helvetica", "bold");
  doc.text(title, x + 4, y + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(15, 15, 15);
  let ly = y + 12;
  for (const l of lines) {
    if (!l) continue;
    const wrapped = doc.splitTextToSize(l, w - 8);
    doc.text(wrapped, x + 4, ly);
    ly += wrapped.length * 4;
  }
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}
